// Section 1 — "Accounts & models" (explains-page-contract.json
// s1_accounts_models): AUM by account colored by role, the models in
// play, per-model asset-class composition, and the structural-fact
// callout (conditional on a non-empty unreachable set).
import { Card, Table } from "react-bootstrap";
import { formatCurrency, formatPercent } from "../../../../lib/numbers";
import type { AccountModelInfo } from "../../lib/explainTypes";
import type { SoftSectionContext } from "./SoftCompletionContext";
import {
  CHART_COLORS,
  ChartLegend,
  HBarChart,
  buildColorScale,
  type HBarRow,
} from "./charts";
import {
  groupByAssetClass,
  householdOnlySymbols,
  num,
  ownModelAccountIds,
  residualAccountIds,
  sortedByValueDesc,
  unreachableAcsForAccount,
} from "./softCompletionLib";

interface ModelRow {
  modelId: number | null;
  name: string;
  primary: Record<string, string>;
  accountIds: number[];
  kind: string;
}

// Distinct own models (from accounts with an assigned model) plus the
// household model row that the no-model accounts follow.
function modelsInPlay(ctx: SoftSectionContext): ModelRow[] {
  const rows = new Map<string, ModelRow>();
  for (const accountId of ownModelAccountIds(ctx.completion)) {
    const model: AccountModelInfo | undefined =
      ctx.accountsById.get(accountId)?.model;
    if (!model) continue;
    const key = String(model.id ?? model.name ?? accountId);
    const row = rows.get(key) ?? {
      modelId: model.id,
      name: model.name ?? "—",
      primary: model.primary ?? {},
      accountIds: [],
      kind: "own model",
    };
    row.accountIds.push(accountId);
    rows.set(key, row);
  }
  const residualIds = residualAccountIds(ctx.completion);
  if (ctx.householdModel && residualIds.length > 0) {
    rows.set("household", {
      modelId: ctx.householdModel.id,
      name: ctx.householdModel.name ?? "Household model",
      primary: ctx.householdModel.primary ?? {},
      accountIds: residualIds,
      kind: "household default → no-model account",
    });
  }
  return [...rows.values()];
}

export default function AccountsModelsSection({
  ctx,
}: {
  ctx: SoftSectionContext;
}) {
  const { completion, accountsById, resolveAccountName } = ctx;
  const aum = num(completion.household_aum);
  const models = modelsInPlay(ctx);

  // One stable color per own model; no-model accounts share gray.
  const modelColor = buildColorScale(
    models.filter((m) => m.kind === "own model").map((m) => String(m.modelId)),
  );

  const aumRows: HBarRow[] = [...(completion.account_classifications ?? [])]
    .sort((a, b) => num(b.balance) - num(a.balance))
    .map((c) => {
      const account = accountsById.get(c.account_id);
      const isOwnModel = c.processing_group === "model";
      const balance = num(c.balance);
      return {
        label: resolveAccountName(c.account_id),
        sub: isOwnModel
          ? `${account?.is_taxable ? "Taxable" : "Tax-exempt"} · ${account?.model?.name ?? `model ${c.model_id ?? "—"}`}`
          : "no-model",
        segments: [
          {
            value: balance,
            color: isOwnModel
              ? modelColor(String(c.model_id))
              : CHART_COLORS.cash,
          },
        ],
        note: `${formatCurrency(balance)} · ${aum > 0 ? formatPercent(balance / aum, 1) : "—"}`,
      };
    });

  // Shared asset-class order for the composition chart: the same order
  // the household-budget bars use (household_target_by_asset_class,
  // descending dollars), then any classes only the account models hold.
  // Every bar's segments and the legend follow it, so the legend reads
  // left-to-right with the chart and matches the other widgets.
  const compositionAcOrder: string[] = sortedByValueDesc(
    completion.household_target_by_asset_class,
  ).map(([ac]) => ac);
  for (const m of models) {
    const mix = groupByAssetClass(m.primary, ctx.symbolToAc);
    for (const [ac] of Object.entries(mix)
      .filter(([, v]) => v > 0)
      .sort(([, a], [, b]) => b - a)) {
      if (!compositionAcOrder.includes(ac)) compositionAcOrder.push(ac);
    }
  }

  // Structural fact: unreachable household classes per own model +
  // household-only symbols. Render only when non-empty (contract
  // structural_fact_callout).
  const unreachableByModel = models
    .filter((m) => m.kind === "own model")
    .map((m) => ({
      model: m,
      unreachable: unreachableAcsForAccount(
        m.accountIds[0],
        completion,
        accountsById,
        ctx.symbolToAc,
      ),
    }))
    .filter((e) => e.unreachable.length > 0);
  const hhOnlySymbols = householdOnlySymbols(
    completion,
    ctx.accounts,
    ctx.householdModel?.primary,
  );

  return (
    <Card className="mb-3">
      <Card.Header>Accounts &amp; models</Card.Header>
      <Card.Body>
        <HBarChart
          title={`Household AUM by account — ${formatCurrency(aum)}`}
          rows={aumRows}
          labelWidth={260}
        />
        <ChartLegend
          items={[
            ...models
              .filter((m) => m.kind === "own model")
              .map((m) => ({
                label: `${m.name} (${m.modelId ?? "—"})`,
                color: modelColor(String(m.modelId)),
              })),
            ...(residualAccountIds(completion).length > 0
              ? [
                  {
                    label: "no-model (follows household)",
                    color: CHART_COLORS.cash,
                  },
                ]
              : []),
          ]}
        />

        <Table size="sm" responsive className="mt-3 mb-0">
          <caption className="text-muted small">The models in play</caption>
          <thead>
            <tr>
              <th className="text-end">Model</th>
              <th>Name</th>
              <th className="text-end"># sec</th>
              <th>Accounts</th>
              <th>Kind</th>
            </tr>
          </thead>
          <tbody>
            {models.map((m) => (
              <tr key={`${m.kind}-${m.modelId}`}>
                <td className="text-end">{m.modelId ?? "—"}</td>
                <td>
                  {m.name}
                  {m.kind === "own model" ? "" : " (household model)"}
                </td>
                <td className="text-end">{Object.keys(m.primary).length}</td>
                <td>{m.accountIds.map(resolveAccountName).join(", ")}</td>
                <td>{m.kind}</td>
              </tr>
            ))}
          </tbody>
        </Table>

        <HBarChart
          title="Asset-class composition — household model vs account models"
          rows={models.map((m) => {
            const mix = groupByAssetClass(m.primary, ctx.symbolToAc);
            const total = Object.values(mix).reduce((a, b) => a + b, 0);
            return {
              label: m.name,
              sub: `${m.kind === "own model" ? "account model" : "household"} · ${Object.keys(m.primary).length} sec`,
              segments: Object.entries(mix)
                .filter(([, v]) => v > 0)
                .sort(
                  ([a], [b]) =>
                    compositionAcOrder.indexOf(a) -
                    compositionAcOrder.indexOf(b),
                )
                .map(([ac, v]) => ({
                  value: total > 0 ? (v / total) * 100 : 0,
                  color: ctx.acColor(ac),
                  title: `${ac} ${formatPercent(v, 1)}`,
                })),
            };
          })}
          max={100}
        />
        <ChartLegend
          items={compositionAcOrder.map((ac) => ({
            label: ac,
            color: ctx.acColor(ac),
          }))}
        />

        {(unreachableByModel.length > 0 || hhOnlySymbols.length > 0) && (
          <div
            className="border-start border-3 rounded-end bg-body-tertiary px-3 py-2 mt-3"
            style={{ fontSize: "0.88rem" }}
          >
            <span
              className="d-block text-uppercase fw-bold"
              style={{ fontSize: "0.62rem", letterSpacing: "0.07em" }}
            >
              Structural fact
            </span>
            {unreachableByModel.map(({ model, unreachable }) => (
              <div key={String(model.modelId)}>
                {model.name} cannot reach: {unreachable.join(", ")}
              </div>
            ))}
            {hhOnlySymbols.length > 0 && (
              <div>
                Household-model-only symbols (no account model holds them):{" "}
                {hhOnlySymbols.join(", ")}
              </div>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
